/**
 * useNavigation - Custom hook for navigation state management
 *
 * Provides navigation-related state and utility functions for determining
 * active states and managing collapsible sections with context-aware highlighting.
 *
 * Context-aware highlighting:
 * - "Customers" navigation item highlights when on customer pages OR viewing assigned systems
 * - "Unassigned Systems" highlights when on unassigned systems list OR viewing unassigned system details
 */

import { useLocation, useParams } from "@tanstack/react-router";
import { useState } from 'react';
import { NavItem } from "../components/NavigationItems";

interface NavigationContext {
  /** Whether the current system has a customer assignment */
  hasCustomerAssignment?: boolean;
  /** Whether we're currently loading customer assignment data */
  isLoadingCustomerAssignment?: boolean;
}

export const useNavigation = (context?: NavigationContext) => {
  const location = useLocation();
  const params = useParams({strict: false});
  /** State to track which navigation sections are currently expanded */
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  /**
   * Determines if we're on a system detail page
   */
  const isOnSystemDetailPage = () => {
    return location.pathname.startsWith('/systems/') &&
      location.pathname !== '/systems/unassigned' &&
      'systemId' in params;
  };

  /**
   * Determines if a navigation path should be highlighted as active.
   * Handles both exact matches and subpath matching, with support for excluded paths.
   * Enhanced with context-aware logic for customer assignment.
   */
  const isActive = (path: string, excludePaths?: string[], label?: string) => {
    if (path === '/') return location.pathname === '/';

    const isMatch = location.pathname === path || location.pathname.startsWith(path + '/');

    if (isMatch && excludePaths) {
      return !excludePaths.some(excludePath =>
        location.pathname === excludePath || location.pathname.startsWith(excludePath + '/')
      );
    }

    // Context-aware highlighting for system detail pages
    if (isOnSystemDetailPage() && context && !context.isLoadingCustomerAssignment) {
      // Highlight "Customers" when viewing a system with customer assignment
      if (label === "Customers" && context.hasCustomerAssignment) {
        return true;
      }

      // Highlight "Unassigned Systems" when viewing a system without customer assignment
      if (label === "Unassigned Systems" && !context.hasCustomerAssignment) {
        return true;
      }
    }

    return isMatch;
  };

  /**
   * Checks if a navigation item with children has any active child items.
   * Used to determine if parent sections should be highlighted.
   */
  const hasActiveChildren = (item: NavItem): boolean => {
    if (!item.children) return false;
    return item.children.some(child =>
      child.path && isActive(child.path, child.excludePaths, child.label)
    );
  };

  /** Toggles the expanded/collapsed state of a navigation section */
  const toggleSection = (label: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [label]: !prev[label]
    }));
  };

  /**
   * Determines if an item should be highlighted as active.
   * For items with paths, checks if the path is active (with context awareness).
   * For section headers without paths, checks if any children are active.
   */
  const getItemActiveState = (item: NavItem) => {
    return item.path ? isActive(item.path, item.excludePaths, item.label) : hasActiveChildren(item);
  };

  return {
    expandedSections,
    toggleSection,
    isActive,
    hasActiveChildren,
    getItemActiveState
  };
};